import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Webhook as WebhookIcon,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/EmptyState';
import { Pagination } from '@/components/Pagination';
import { ApiError } from '@/lib/fetcher';
import {
  type WebhookDelivery,
  fetchWebhookDeliveries,
  webhookDeliveriesKey,
} from '@/lib/queries';

const STATUS_VARIANT: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive' | 'success'
> = {
  PENDING: 'secondary',
  // Brief 117 — delivered = green (healthy), not the brand red.
  SUCCESS: 'success',
  FAILED: 'destructive',
};

function DeliveryRow({ delivery }: { delivery: WebhookDelivery }) {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <>
      <tr
        className="cursor-pointer border-b transition-colors hover:bg-muted/50"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-3 py-2">
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </td>
        <td className="px-3 py-2 font-mono text-xs">{delivery.event}</td>
        <td className="px-3 py-2">
          <Badge variant={STATUS_VARIANT[delivery.status] ?? 'secondary'}>
            {delivery.status}
          </Badge>
        </td>
        <td className="px-3 py-2 text-right">{delivery.attempts}</td>
        <td className="px-3 py-2 text-right font-mono text-xs">
          {delivery.responseCode ?? '—'}
        </td>
        <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
          {delivery.lastAttemptAt
            ? format(parseISO(delivery.lastAttemptAt), 'MMM d HH:mm:ss')
            : '—'}
        </td>
        <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
          {delivery.nextRetryAt
            ? format(parseISO(delivery.nextRetryAt), 'MMM d HH:mm:ss')
            : '—'}
        </td>
      </tr>
      {expanded ? (
        <tr className="border-b bg-muted/20">
          <td colSpan={7} className="px-6 py-3 text-xs">
            <dl className="grid gap-2 sm:grid-cols-2">
              <div>
                <dt className="font-medium">Delivery id</dt>
                <dd className="font-mono">{delivery.id}</dd>
              </div>
              <div>
                <dt className="font-medium">Subject id</dt>
                <dd className="font-mono">
                  {delivery.subjectId ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="font-medium">Created</dt>
                <dd>{format(parseISO(delivery.createdAt), 'PPpp')}</dd>
              </div>
              <div>
                <dt className="font-medium">Attempts</dt>
                <dd>{delivery.attempts}</dd>
              </div>
            </dl>
            {delivery.errorMessage ? (
              <div className="mt-3">
                <p className="font-medium">Error</p>
                <pre className="mt-1 overflow-x-auto rounded border bg-background p-2 text-xs">
                  {delivery.errorMessage}
                </pre>
              </div>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function WebhookDeliveries() {
  const { id } = useParams<{ id: string }>();
  const [limit, setLimit] = React.useState(50);
  const [offset, setOffset] = React.useState(0);
  const args = { limit, offset };
  const q = useQuery({
    queryKey: webhookDeliveriesKey(id ?? '', args),
    queryFn: () => fetchWebhookDeliveries(id ?? '', args),
    enabled: Boolean(id),
  });

  if (!id) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Missing webhook id</AlertTitle>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <Button asChild variant="ghost" size="sm" className="-ml-3">
        <Link to="/dashboard/webhooks">
          <ArrowLeft className="mr-1 h-4 w-4" />
          Webhooks
        </Link>
      </Button>

      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Deliveries</h1>
        <p className="text-muted-foreground">
          Every attempt this webhook has made or queued. Click a row to
          expand.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Recent deliveries</CardTitle>
          <CardDescription>
            Failed deliveries retry with exponential backoff up to the
            tunable max-attempts cap.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {q.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : q.error ? (
            <Alert variant="destructive">
              <AlertTitle>
                Couldn't load deliveries (
                {q.error instanceof ApiError ? q.error.code : 'unknown'})
              </AlertTitle>
              <AlertDescription>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => q.refetch()}
                >
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : q.data && q.data.data.length === 0 ? (
            <EmptyState
              icon={WebhookIcon}
              title="No deliveries yet"
              description="Once Relaystation fires an event matching this webhook's filter, attempts will appear here."
            />
          ) : q.data ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="px-3 py-2 w-8" />
                      <th className="px-3 py-2">Event</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2 text-right">Attempts</th>
                      <th className="px-3 py-2 text-right">HTTP</th>
                      <th className="px-3 py-2">Last attempt</th>
                      <th className="px-3 py-2">Next retry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.data.map((d) => (
                      <DeliveryRow key={d.id} delivery={d} />
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                total={q.data.total}
                limit={q.data.limit}
                offset={q.data.offset}
                onChange={({ limit: nl, offset: no }) => {
                  setLimit(nl);
                  setOffset(no);
                }}
              />
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
